<script setup lang="ts">
import { computed, ref, nextTick } from 'vue';
import { ChevronRight } from 'lucide-vue-next';
import { ColumnDef } from '@tanstack/vue-table';
import { DataTable, DataTableFilter } from '@/components/ui/data-table';
import { Card } from '@/components/ui/card';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { Button } from '@/components/ui/button';
import Separator from '@/components/ui/separator/Separator.vue';
import { Record, RecordForm, MultiStepFormState } from '@/types';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { useRecords, useRecordTypes } from '@/plugins/store/actions/queries/records';
import { useRecordsSearchParams } from '@/composables/records/useRecordsSearchParams';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import { Skeleton } from '@/components/ui/skeleton';
import TableCalendarModal from './TableCalendarModal.vue';
import { i18n } from '@/plugins/internationalization/i18n';

const props = withDefaults(
    defineProps<{
        selectableRecordTypeIds?: number[];
        fetchOwnRecords?: boolean;
    }>(),
    {
        selectableRecordTypeIds: () => [],
        title: '',
        fetchOwnRecords: true,
    }
);

const { data: recordTypes, isLoading: isRecordTypesLoading } = useRecordTypes();
const filteredRecordTypes = computed(() => recordTypes.value?.filter((t) => props.selectableRecordTypeIds.includes(t.id)) || []);
const typeOptions = computed(() =>
    filteredRecordTypes.value.map((t) => ({
        value: t.id.toString(),
        label: t.name,
        icon_url: t.icon_url,
    }))
);

const { fetchParams, page, pageSize, pageSizeOptions, types } = useRecordsSearchParams();

const computedTypes = computed({
    get() {
        return filteredRecordTypes.value.filter((t) => fetchParams.value.typeIds?.includes(t.id)).map((t) => t.id.toString()) || [];
    },
    set(value) {
        types.value = value.map(Number);
    },
});

const filteredFetchParams = computed(() => {
    // If user has selected specific types, filter to only include those that are in selectableRecordTypeIds
    // Otherwise, use all selectableRecordTypeIds
    const selectedTypeIds = fetchParams.value.typeIds?.length > 0 ? fetchParams.value.typeIds.filter((id) => props.selectableRecordTypeIds.includes(id)) : props.selectableRecordTypeIds;

    return {
        ...fetchParams.value,
        typeIds: selectedTypeIds,
    };
});

const isQueryEnabled = computed(() => filteredFetchParams.value.typeIds.length > 0 && filteredFetchParams.value.siteIds.length > 0);
const { data: recordsResponse, isFetching: isRecordsLoading } = useRecords(filteredFetchParams, { enabled: isQueryEnabled });
const isLoading = computed(() => isRecordTypesLoading.value || isRecordsLoading.value);

const recordsData = computed(
    () =>
        recordsResponse.value?.data.map((r) => {
            const recordType = filteredRecordTypes.value.find((t) => t.id === r.type_id);
            return {
                ...r,
                name: recordType?.name,
                icon_url: recordType?.icon_url,
            };
        }) || []
);

const pagination = computed(() => ({
    totalPages: recordsResponse.value?.total_pages || 0,
    totalRows: recordsResponse.value?.total_rows || 0,
}));

const columns = computed<ColumnDef<Record & { name?: string; icon_url?: string }>[]>(() => [
    {
        header: i18n.global.t('views.activityTable.table.headers.type'),
        accessorKey: 'name',
        enableSorting: false,
    },
    {
        header: i18n.global.t('views.activityTable.table.headers.date'),
        accessorKey: 'date',
        enableSorting: false,
    },
    {
        header: i18n.global.t('views.activityTable.table.headers.amount'),
        accessorKey: 'amount',
        enableSorting: false,
    },
    {
        header: i18n.global.t('views.activityTable.table.headers.description'),
        accessorKey: 'description',
        enableSorting: false,
    },
    {
        header: '',
        accessorKey: '_actions',
    },
]);

const actionModalStepConfig = ref([{}, { deleteButton: true, editable: true }, { submitActionType: ActionType.PutRecordForm }, {}]);
const actionModalInitialState = ref<MultiStepFormState>({
    location: null,
    form: null,
    confirmed: false,
    farm: null,
    event: null,
    notification: null,
});
const actionModalHeader = ref<{ icon_url: string; text: string } | null>(null);

const loadingRecordId = ref<number | null>(null);

async function onRecordClicked(record: Record & { name?: string; icon_url?: string }, open: CallableFunction) {
    if (loadingRecordId.value !== null) {
        return;
    }

    try {
        loadingRecordId.value = record.id;

        const recordForm: RecordForm = await store.dispatch(ActionType.GetRecordForm, { form: { record_id: record.id } });

        actionModalInitialState.value = {
            ...actionModalInitialState.value,
            form: recordForm.form,
            record_id: record.id,
        };
        actionModalHeader.value = { icon_url: record.icon_url || '', text: record.name || '' };
        actionModalStepConfig.value[1].deleteButton = recordForm.deletable;
        actionModalStepConfig.value[1].editable = recordForm.editable;

        await nextTick();

        open();
    } finally {
        loadingRecordId.value = null;
    }
}
</script>

<template>
    <div class="space-y-5">
        <Separator />
        <div class="space-y-5">
            <div class="flex flex-wrap justify-end items-center gap-3">
                <FadeTransition mode="out-in">
                    <div v-if="isRecordTypesLoading" class="flex gap-2 items-center">
                        <Skeleton class="w-20 h-10 bg-gray-200" />
                    </div>
                    <div v-else class="flex gap-2 items-center">
                        <DataTableFilter
                            v-model="computedTypes"
                            :options="typeOptions"
                            :title="$t('views.activityTable.filterModal.header')"
                            item-value="value"
                            item-label="label"
                            update-only-on-close
                            align="end"
                        >
                            <template #item="{ item }">
                                <div class="flex items-center gap-2">
                                    <Icon :src="item.icon_url" class="w-6 h-6" />
                                    <div>{{ item.label }}</div>
                                </div>
                            </template>
                        </DataTableFilter>
                    </div>
                </FadeTransition>
                <TableCalendarModal :selectable-record-type-ids="selectableRecordTypeIds" />
            </div>
            <Card class="overflow-hidden">
                <ActionModal :initial-step-index="1" :initial-state="actionModalInitialState" :step-config="actionModalStepConfig" :header="actionModalHeader">
                    <template #default="{ open }">
                        <DataTable
                            v-model:page="page"
                            v-model:page-size="pageSize"
                            :data="recordsData"
                            :columns="columns"
                            :loading="isLoading"
                            :page-size-options="pageSizeOptions"
                            :total-pages="pagination.totalPages"
                            :total-rows="pagination.totalRows"
                            server-side
                        >
                            <template #item[name]="{ item }">
                                <div class="flex items-center gap-2.5 transition-opacity duration-200 hover:opacity-80 cursor-pointer" @click="onRecordClicked(item, open)">
                                    <Icon v-if="item.icon_url" :src="item.icon_url" class="w-9 h-9" />
                                    <div>{{ item.name }}</div>
                                </div>
                            </template>
                            <template #item[date]="{ item }">
                                {{ $d(new Date(item.date), 'short') }}
                            </template>
                            <template #item[amount]="{ item }">
                                {{ item.amount }}
                            </template>
                            <template #item[description]="{ item }">
                                {{ item.description }}
                            </template>
                            <template #item[_actions]="{ item }">
                                <div class="flex justify-end">
                                    <Button variant="ghost" size="icon" class="hover:gray-300 rounded-full -mr-1" :disabled="loadingRecordId !== null" @click="onRecordClicked(item, open)">
                                        <Spinner v-if="loadingRecordId === item.id" class="h-5 w-5" aria-hidden="true" />
                                        <ChevronRight v-else class="h-5 w-5" aria-hidden="true" />
                                    </Button>
                                </div>
                            </template>
                        </DataTable>
                    </template>
                </ActionModal>
            </Card>
        </div>
    </div>
</template>
